import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import api from 'apis/api';

import { ConsolidationSet, Entity, useEntityStore } from 'store/entity.store';
import { useWorkspaceStore } from 'store/workspace.store';

/**
 * React Query hook for entities that works alongside the existing store
 * This provides caching without interfering with existing loading logic
 */
export function useEntitiesQuery() {
  const { workspace } = useWorkspaceStore();

  return useQuery<Entity[]>({
    queryKey: ['entities', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) {
        throw new Error('Workspace ID is required');
      }

      const response = await api.get(
        `/get/all/entities?organisation_id=${workspace.id}`
      );

      if (!response.data.message || !Array.isArray(response.data.message)) {
        throw new Error('Invalid data format received');
      }

      return response.data.message[0] || [];
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}

/**
 * React Query hook for consolidation sets
 */
export function useConsolidationSetsQuery() {
  const { workspace } = useWorkspaceStore();

  return useQuery<ConsolidationSet[]>({
    queryKey: ['consolidation-sets', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) {
        throw new Error('Workspace ID is required');
      }

      const response = await api.get(
        `/get/all/consolidation_sets?organisation_id=${workspace.id}`
      );

      if (!response.data.message || !Array.isArray(response.data.message)) {
        throw new Error('Invalid data format received');
      }

      return response.data.message || [];
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}

/**
 * Hook that manages both queries and syncs to store
 * Use this in components that need entity data
 */
export function useEntityData() {
  const { setEntities, setConsolidationSets } = useEntityStore();
  const entitiesQuery = useEntitiesQuery();
  const consolidationSetsQuery = useConsolidationSetsQuery();

  // Only sync to store when we have successful data
  useEffect(() => {
    if (entitiesQuery.data && entitiesQuery.isSuccess) {
      setEntities(entitiesQuery.data);
    }
  }, [entitiesQuery.data, entitiesQuery.isSuccess, setEntities]);

  useEffect(() => {
    if (consolidationSetsQuery.data && consolidationSetsQuery.isSuccess) {
      setConsolidationSets(consolidationSetsQuery.data);
    }
  }, [
    consolidationSetsQuery.data,
    consolidationSetsQuery.isSuccess,
    setConsolidationSets,
  ]);

  return {
    entitiesQuery,
    consolidationSetsQuery,
    isLoading: entitiesQuery.isLoading || consolidationSetsQuery.isLoading,
    refetch: () => {
      return Promise.all([
        entitiesQuery.refetch(),
        consolidationSetsQuery.refetch(),
      ]);
    },
  };
}
